import { type ReactNode } from 'react';
import type { AvatarProps } from './Avatar';
import { Avatar } from './Avatar';
import { ClippedTextTooltip } from './ClippedTextTooltip';
import { PLAN_LEVEL_MAP, type PlanLevel } from '~/lib/.client/plans';
import type { OrganizationMembershipLevel } from '~/types/organization';
import type { UserMembershipLevel } from '~/types/user';
import { classNames } from '~/utils/classNames';

export interface AccountDisplayProps {
  title: ReactNode;
  description?: ReactNode;
  user?: AvatarProps['user'];
  accentLine?: 'first' | 'second';
  avatarFallback?: AvatarProps['fallback'];
  className?: string;
  lineClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
}

const baseLineClass = 'text-sm min-w-0 truncate w-full max-w-56 text-left';
const activeLineClass = 'font-semibold';
const inactiveLineClass = 'text-bolt-ds-textTertiary';

export const AccountDisplay = ({
  user,
  title,
  description,
  accentLine = 'first',
  className,
  avatarFallback = 'icon',
  lineClassName = '',
  titleClassName,
  descriptionClassName,
}: AccountDisplayProps) => {
  return (
    <div className={classNames('flex items-center gap-3 max-w-full min-w-0', className)}>
      <Avatar size="md" user={user} fallback={avatarFallback} />

      <div className={classNames('flex flex-col items-start w-full overflow-hidden')}>
        <ClippedTextTooltip
          text={title}
          contentClassName="text-sm text-bolt-ds-textTertiary"
          triggerClassName={classNames(
            baseLineClass,
            accentLine === 'first' ? activeLineClass : inactiveLineClass,
            lineClassName,
            titleClassName,
          )}
        />
        {description && (
          <ClippedTextTooltip
            text={description}
            contentClassName="text-sm text-bolt-ds-textTertiary"
            triggerClassName={classNames(
              baseLineClass,
              accentLine === 'second' ? activeLineClass : inactiveLineClass,
              lineClassName,
              descriptionClassName,
            )}
          />
        )}
      </div>
    </div>
  );
};

export interface AccountDisplayWithMembershipProps {
  type: 'user' | 'organization';
  user?: AvatarProps['user'];
  membershipLevel?: UserMembershipLevel | OrganizationMembershipLevel;
  accountName: string;
  orgName?: string;
  avatar?: string;
}

export const AccountDisplayWithMembership = ({
  type,
  user,
  membershipLevel,
  accountName,
  orgName,
  avatar,
}: AccountDisplayWithMembershipProps) => {
  return (
    <AccountDisplay
      user={{
        name: user?.name || '',
        avatar: avatar || user?.avatar,
      }}
      title={accountName}
      description={
        <>
          {Boolean(membershipLevel && membershipLevel > 0) && (
            <span className="mr-1 text-bolt-ds-successHighlight">
              {type === 'user' ? 'Pro' : 'Teams'}{' '}
              <span className="font-semibold">{PLAN_LEVEL_MAP[`pro_${membershipLevel}` as PlanLevel].namePrice}</span>
            </span>
          )}
          <span>{type === 'user' ? 'Personal Plan' : orgName}</span>
        </>
      }
    />
  );
};
